import { CalendarPlus } from 'lucide-react';
import * as React from 'react';

import { CALENDAR_ACTION_LABEL, calendarEntry, calendarFileName } from './constants';

/**
 * **Add to calendar** (`MEM-017` AC-2), in the event header card beside *View your ticket*.
 *
 * Sumit, reviewing #528: the calendar control moves *"under the date in the header"*. It is the
 * same link treatment the ticket control uses, with a `calendar-plus` glyph, so the two read as one
 * row of things a member who is going does with this event.
 *
 * **The entry is unchanged**, including the part with the trap in it: `calendarEntry` writes UTC
 * instants with the event's own timezone, so a member's phone cannot reinterpret the hour
 * (`MEM-017` AC-2, `docs/standards/time-and-timezone.md`). It renders nothing when the event has no
 * start instant to write.
 */
export function AddToCalendarControl({
  eventName,
  startsAt,
  endsAt,
  timezone,
  venueName,
  confirmationCode,
}: {
  readonly eventName: string;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly timezone: string | null;
  readonly venueName: string | null;
  readonly confirmationCode: string;
}): React.ReactElement | null {
  const entry = calendarEntry({
    name: eventName,
    startsAt,
    endsAt,
    timezone,
    venueName,
    confirmationCode,
  });
  if (entry === null) return null;

  return (
    <button
      type="button"
      onClick={() => {
        downloadCalendarEntry(entry, calendarFileName(eventName));
      }}
      className="inline-flex items-center gap-[5px] font-brand text-sm font-bold text-primary"
    >
      <CalendarPlus className="h-[15px] w-[15px]" aria-hidden />
      {CALENDAR_ACTION_LABEL}
    </button>
  );
}

/**
 * Hand the entry to the browser as a file.
 *
 * A blob URL rather than a `data:` URI: iOS Safari refuses to download a `data:` URI of this type,
 * and the calendar entry is most useful on the phone the member will be holding at the door.
 */
function downloadCalendarEntry(entry: string, fileName: string): void {
  const blob = new Blob([entry], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}
